import { filter, some } from 'lodash';
import { FC, useState } from 'react';
import QuizService from '../../../services/quiz.service';
import { useParams } from 'react-router-dom';
import useBrowserStorage from '../../../hooks/use-browser-storage';
import { setToastInfo } from '../../../store/common.slice';
import { useAppDispatch } from '../../../hooks/redux-hooks';
import { ToastType } from '../../../types/common.types';
import { SelectedFile } from '../../../types/agent.types';
import { Trash2 } from 'lucide-react';
import { inputFieldStyles } from '../../../global-styles';
import { TFilesData } from '@answerr/types';

type ManageQuizFilesProps = {
  filesData: TFilesData[];
  scrapedUrls: string[];
  refreshFilesData: () => void;
  readOnly?: boolean;
};

type CombinedData = {
  _id: string;
  name?: string;
  url?: string;
  fileId?: string;
};

const ManageQuizFiles: FC<ManageQuizFilesProps> = ({
  filesData,
  scrapedUrls,
  refreshFilesData,
  readOnly
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const { getItem } = useBrowserStorage();
  const { quizId } = useParams();
  const orgId = JSON.parse(getItem('orgInfo', 'local') || '{}')?._id;
  const dispatch = useAppDispatch();

  const combinedData: CombinedData[] = [
    ...filesData.map((file) => ({
      _id: file._id as string,
      name: file.name,
      fileId: file.fileId,
    })),
    ...scrapedUrls.map((url, index) => ({
      _id: `url-${index}`,
      url: url || '',
    })),
  ];

  const filteredFilesData = combinedData.filter((item) =>
    item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    item.url?.toLowerCase().includes(searchText.toLowerCase())
  );

  const deleteFiles = async () => {
    const data = {
      quizId: quizId as string,
      files: selectedFiles,
      orgId: orgId,
    };
    try {
      const response = await QuizService.deleteFiles(data);
      if (response.data.error) {
        dispatch(
          setToastInfo({
            isOpen: true,
            type: ToastType.ERROR,
            message: response.data.message,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            isOpen: true,
            type: ToastType.SUCCESS,
            message: 'File(s) deleted successfully.',
          })
        );
      }
      await refreshFilesData();
      setSelectedFiles([]);
    } catch (err) {
      dispatch(
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: 'Error in deleting data.',
        })
      );
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex mb-5">
        <div className="flex-grow pr-3">
          <input
            type="search"
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            value={searchText}
            className={inputFieldStyles}
            placeholder="Search Files"
            disabled={readOnly}
          />
        </div>

        <button
          className="
          bg-white px-5 text-sm text-red-600 border flex items-center gap-2
          border-[rgba(0,0,0,.1)] rounded-lg disabled:opacity-60 h-[38px]"
          disabled={!selectedFiles.length || readOnly}
          onClick={() => deleteFiles()}
        >
          <Trash2 color="#DC2626" size={15} />
          <span className="text-[#DC2626] text-[14px]">Delete</span>
        </button>
      </div>

      <table
        border={1}
        className="w-full border border-appLightBlue rounded-xl shadow-sm"
      >
        <thead>
          <tr>
            <th className="w-[100px] text-left px-3 py-2">
              <input
                type="checkbox"
                checked={selectedFiles.length === filteredFilesData.length && filteredFilesData.length > 0}
                className="cursor-pointer"
                disabled={readOnly}
                onChange={() => {
                  if (selectedFiles.length === filteredFilesData.length) {
                    setSelectedFiles([]);
                  } else {
                    setSelectedFiles([
                      ...filteredFilesData.map((file) => ({
                        id: file._id || '',
                        fileId: file.fileId || '',
                        url: file.url || '',
                      })),
                    ]);
                  }
                }}
              />
            </th>
            <th className="text-[15px] font-medium text-left px-3 py-2 text-appTextLight">
              File name
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredFilesData.length === 0 ? (
            <tr>
              <td colSpan={2} className="text-center py-8 text-gray-500">
                No files or URLs added yet
              </td>
            </tr>
          ) : (
            [...filteredFilesData].reverse().map((item) => (
              <tr key={item._id}>
                <td className="w-[100px] text-left px-3 py-2">
                  <input
                    type="checkbox"
                    checked={some(selectedFiles, (file) => file.id === item._id)}
                    className="cursor-pointer"
                    disabled={readOnly}
                    onChange={() => {
                      const currentFile = {
                        id: item._id || '',
                        fileId: item.fileId || '',
                        url: item.url || '',
                      };
                      const hasId = some(
                        selectedFiles,
                        (file) => file.id === currentFile.id
                      );
                      if (hasId) {
                        setSelectedFiles(
                          filter(
                            selectedFiles,
                            (file) => file.id !== currentFile.id
                          )
                        );
                      } else {
                        setSelectedFiles([...selectedFiles, currentFile]);
                      }
                    }}
                  />
                </td>
                <td className="px-3 py-2 text-left">
                  <p className="text-sm truncate">
                    {item.name?.split('_')[1]
                      ? item.name?.split('_')[1]
                      : item.url
                      ? item.url.slice(0, 40)
                      : item.name}
                  </p>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ManageQuizFiles;
